import React, { useState, useEffect } from 'react';
import { Play, Pause, Square, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../features/auth/authStore';
import { useTimer } from '../../contexts/TimerContext';
import toast from 'react-hot-toast';

interface ActiveTimer {
  id: string;
  taskId?: string;
  taskTitle?: string;
  description: string;
  startTime: Date;
  duration: number;
  billable: boolean;
  status: string;
}

interface Task {
  id: string;
  title: string;
  status: string;
}

interface TimeTrackerWidgetProps {
  userRole: string;
  availableTasks?: Task[];
  onTimerUpdate?: (timer: ActiveTimer | null) => void;
  className?: string;
  compact?: boolean;
}

export const TimeTrackerWidget: React.FC<TimeTrackerWidgetProps> = ({
  userRole,
  availableTasks = [],
  onTimerUpdate,
  className = '',
  compact = false
}) => {
  const { activeTimer, elapsedTime, loading, startTimer: startTimerContext, stopTimer: stopTimerContext } = useTimer();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedTask, setSelectedTask] = useState<string>('');
  const [description, setDescription] = useState('');

  // Notify parent of timer updates
  useEffect(() => {
    if (onTimerUpdate) {
      onTimerUpdate(activeTimer);
    }
  }, [activeTimer, onTimerUpdate]);

  const startTimer = async () => {
    if (!selectedTask && availableTasks.length > 0 && !compact) {
      toast.error('Please select a task');
      return;
    }

    try {
      await startTimerContext(selectedTask || undefined, description || 'Timer session');

      if (!compact) {
        setIsExpanded(false);
      }

      // Reset form
      setSelectedTask('');
      setDescription('');
    } catch (error: any) {
      console.error('Error starting timer:', error);
    }
  };

  const stopTimer = async () => {
    if (!activeTimer) return;

    try {
      await stopTimerContext();

      // Reset form
      setDescription('');
      setSelectedTask('');
    } catch (error: any) {
      console.error('Error stopping timer:', error);
    }
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (compact) {
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      }
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    return `${minutes}m ${secs}s`;
  };

  const getTaskTitle = (taskId: string, taskTitle?: string): string => {
    // First try to use the task title from the timer data
    if (taskTitle) {
      return taskTitle;
    }
    // Fallback to looking up in availableTasks
    const task = availableTasks.find(t => t.id === taskId);
    return task ? task.title : 'Unknown Task';
  };

  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Clock className="w-4 h-4 text-gray-500" />
        <div className="font-mono text-sm font-medium">
          {formatDuration(elapsedTime)}
        </div>
        {activeTimer ? (
          <button
            onClick={stopTimer}
            disabled={loading}
            className="flex items-center px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
          >
            <Square className="w-3 h-3 mr-1" />
            Stop
          </button>
        ) : (
          <button
            onClick={startTimer}
            disabled={loading}
            className="flex items-center px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
          >
            <Play className="w-3 h-3 mr-1" />
            Start
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-lg ${className}`}>
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center">
          <Clock className="w-5 h-5 text-gray-500 mr-2" />
          <div>
            <div className="font-mono text-lg font-bold text-gray-900">
              {formatDuration(elapsedTime)}
            </div>
            {activeTimer && (
              <div className="text-xs text-gray-600">
                {activeTimer.taskId ? getTaskTitle(activeTimer.taskId, activeTimer.taskTitle) : 'No task selected'}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {activeTimer ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                stopTimer();
              }}
              disabled={loading}
              className="flex items-center px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
            >
              <Square className="w-3 h-3 mr-1" />
              Stop
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (availableTasks.length === 0) {
                  startTimer();
                } else {
                  setIsExpanded(true);
                }
              }}
              disabled={loading}
              className="flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
            >
              <Play className="w-3 h-3 mr-1" />
              Start
            </button>
          )}
          
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded Controls */}
      {isExpanded && !activeTimer && (
        <div className="border-t p-4 space-y-4">
          {availableTasks.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Task
              </label>
              <select
                value={selectedTask}
                onChange={(e) => setSelectedTask(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">No specific task</option>
                {availableTasks.map(task => (
                  <option key={task.id} value={task.id}>
                    {task.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What are you working on?"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <button
            onClick={startTimer}
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Play className="w-4 h-4 mr-2" />
            Start Timer
          </button>
        </div>
      )}
    </div>
  );
};
